import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, X, ShoppingCart, AlertCircle } from 'lucide-react';

const Notification = ({ message, type = 'success', isVisible, onClose, duration = 4000 }) => {
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (isVisible) {
      setProgress(100);
      startTimeRef.current = Date.now();

      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);

        if (remaining <= 0) {
        onClose();
        }
      }, 16); // ~60fps for smooth animation

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      setProgress(100);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-6 right-6 z-50 animate-slide-up max-w-md w-full sm:w-auto">
      <div className={`relative flex items-center gap-4 px-5 py-4 rounded-xl shadow-2xl transform transition-all duration-300 ease-out overflow-hidden bg-white border ${
        type === 'success' 
          ? 'border-green-500 border-l-4' 
          : type === 'error' 
          ? 'border-red-500 border-l-4' 
          : 'border-secondary border-l-4'
      } ${isVisible ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-4 opacity-0 scale-95'}`}>
        
        {/* Progress bar at the bottom */}
        <div 
          className={`absolute bottom-0 left-0 h-1 transition-all ease-linear ${
            type === 'success' 
              ? 'bg-green-500' 
              : type === 'error' 
              ? 'bg-red-500' 
              : 'bg-secondary'
          }`}
          style={{ 
            width: `${progress}%`,
            transition: 'width 0.1s linear'
          }}
        />
        
        {/* Icon */}
        <div className={`flex-shrink-0 z-10 ${
          type === 'success' 
            ? 'text-green-500' 
            : type === 'error' 
            ? 'text-red-500' 
            : 'text-secondary'
        }`}>
          {type === 'success' ? (
            <ShoppingCart className="w-6 h-6" />
          ) : type === 'error' ? (
            <AlertCircle className="w-6 h-6" />
          ) : (
            <CheckCircle className="w-6 h-6" />
          )}
        </div>
        
        {/* Message */}
        <span className="font-poppins font-semibold text-sm md:text-base flex-1 pr-2 z-10 text-gray-800">
          {message}
        </span>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="flex-shrink-0 rounded-full p-1.5 hover:bg-gray-100 transition-all duration-200 group z-10"
          aria-label="Close notification"
        >
          <X className="w-4 h-4 text-gray-600 group-hover:text-gray-900 group-hover:rotate-90 transition-transform duration-200" />
        </button>
      </div>
    </div>
  );
};

export default Notification;
